import {
  CONTOUR_PADDING_FRAMES,
  SEGMENT_FULL_RENDER_CHANGED_RATIO,
  SILENCE_BOUNDARY_MIN_FRAMES,
} from "@/constants/pitchCorrection/segmentRender";
import { FrameRange } from "@/types/defs/pitchCorrection/segmentRender";

// Walks left from the changed range to the nearest qualifying silence run (≥ SILENCE_BOUNDARY_MIN_FRAMES unvoiced frames) and cuts at its middle, so the splice seam always lands in silence. Falls back to the editable start — the clip edge, where the splice applies no fade — when no run exists.
const findCutIndexLeft = (
  frameValues: number[],
  fromIndex: number,
  editableStartIndex: number
): number => {
  let index = fromIndex - 1;
  while (index >= editableStartIndex) {
    if (frameValues[index] === 0) {
      const runEnd = index;
      while (index >= editableStartIndex && frameValues[index] === 0) {
        index--;
      }
      const runStart = index + 1;
      const runLength = runEnd - runStart + 1;
      if (runLength >= SILENCE_BOUNDARY_MIN_FRAMES) {
        return runStart + Math.floor(runLength / 2);
      }
    } else {
      index--;
    }
  }
  return editableStartIndex;
};

// Mirror of findCutIndexLeft: picks an exclusive end boundary at the middle of the nearest silence run right of the changed range, else the editable end.
const findCutIndexRight = (
  frameValues: number[],
  fromIndex: number,
  editableEndIndex: number
): number => {
  let index = fromIndex;
  while (index < editableEndIndex) {
    if (frameValues[index] === 0) {
      const runStart = index;
      while (index < editableEndIndex && frameValues[index] === 0) {
        index++;
      }
      const runLength = index - runStart;
      if (runLength >= SILENCE_BOUNDARY_MIN_FRAMES) {
        return runStart + Math.floor(runLength / 2);
      }
    } else {
      index++;
    }
  }
  return editableEndIndex;
};

// Expands the changed range outward to the nearest silence run on each side, however far, so both splice seams are guaranteed to land in silence (or at a clip edge, where no seam is needed). The scan never enters the analysis padding: a boundary inside it would ask the backend for padded frames it can't align. Returns null when the resulting chunk is so large that a targeted segment render is no longer worthwhile — the caller then does a full render.
export const expandFrameRangeForSplicing = ({
  changedRange,
  frameValues,
  editableFrameCount,
}: {
  changedRange: FrameRange;
  // Padded contour values in wire coordinates where 0 marks an unvoiced frame.
  frameValues: number[];
  editableFrameCount: number;
}): FrameRange | null => {
  const editableStartIndex = CONTOUR_PADDING_FRAMES;
  const editableEndIndex = CONTOUR_PADDING_FRAMES + editableFrameCount;

  const startIndex = findCutIndexLeft(
    frameValues,
    changedRange.startIndex,
    editableStartIndex
  );
  const endIndex = findCutIndexRight(
    frameValues,
    changedRange.endIndex,
    editableEndIndex
  );

  const chunkFrameCount = endIndex - startIndex;
  if (
    chunkFrameCount / editableFrameCount >
    SEGMENT_FULL_RENDER_CHANGED_RATIO
  ) {
    return null;
  }

  return { startIndex, endIndex };
};
